// NOT-277: terms are free text authored by the tenant and rendered verbatim to
// the customer (public quote/invoice/proposal + PDFs). Some tenant templates
// carry fill-in-the-blank clauses (e.g. "A deposit of [__]% (or $[___]) is due…")
// that were never filled in, leaking a raw placeholder to the customer. These
// helpers strip unfilled blanks before the terms reach a customer-facing surface.
//
// Conservative by design: a "blank" is a bracket containing only underscores,
// dashes or whitespace (optionally prefixed with `$` / suffixed with `%`). Named
// placeholders that a tenant fills manually (e.g. "[Company Name]", "[x]") contain
// letters and are left untouched.

import { isHtmlContent } from '@/lib/utils';

// Non-global instance for `.test()` (a global regex keeps `lastIndex` state).
const BLANK_PLACEHOLDER = /\$?\[[_\s–—-]*\]\s*%?/;
const BLANK_PLACEHOLDER_GLOBAL = /\$?\[[_\s–—-]*\]\s*%?/g;

/**
 * Block elements the editor schema can produce that hold a clause. A blank is
 * dealt with inside the innermost one of these that contains it, so removing a
 * ruined clause never takes a sibling clause with it.
 */
const CLAUSE_BLOCK_SELECTOR = 'p, li, h1, h2, h3, h4, h5, h6, blockquote';

/** Containers that are meaningless once every clause inside them is gone. */
const EMPTIABLE_CONTAINER_SELECTOR = 'ul, ol, blockquote, li';

function hasBlankPlaceholder(text: string): boolean {
  return BLANK_PLACEHOLDER.test(text);
}

/**
 * Plain-text terms: drop any sentence that still contains an unfilled blank, so
 * the customer never sees a half-empty clause like "A deposit of % is due…".
 * Lines emptied by this are removed; original blank lines (paragraph breaks) stay.
 */
export function sanitizeClientTerms(terms?: string | null): string {
  if (!terms) return '';
  const lines = terms.split(/\r?\n/);
  return lines
    .map((line) => {
      if (!hasBlankPlaceholder(line)) return line;
      const sentences = line.split(/(?<=[.!?])\s+/);
      return sentences
        .filter((sentence) => !hasBlankPlaceholder(sentence))
        .join(' ')
        .trim();
    })
    .filter((line, index) => line.trim() !== '' || lines[index].trim() === '')
    .join('\n')
    .trim();
}

/** Drop lists/quotes/items left with nothing in them once clauses were removed. */
function pruneEmptyContainers(root: HTMLElement): void {
  // Repeated because emptying an <li> can empty its <ul>, which can in turn
  // empty an <li> of the list above it.
  let removedAny = true;
  while (removedAny) {
    removedAny = false;
    for (const el of Array.from(root.querySelectorAll(EMPTIABLE_CONTAINER_SELECTOR))) {
      if (el.children.length === 0 && !(el.textContent ?? '').trim()) {
        el.remove();
        removedAny = true;
      }
    }
  }
}

/**
 * HTML terms — the normal path, since every T&C repository template is authored
 * in the rich-text editor.
 *
 * Makes the same promise as {@link sanitizeClientTerms}: the customer never
 * reads a clause that was left half-written. Stripping only the `[__]` token
 * (what this used to do) cleared the brackets but still rendered "A deposit of
 * is required." — the bug, not the fix.
 *
 * Works on the parsed document rather than the markup string, so a ruined
 * clause is removed within its own block and its neighbours keep their markup.
 * A block that mixes a ruined clause with sound ones keeps the sound wording as
 * text — the surviving sentences are re-joined, so inline formatting *inside
 * that one block* is not preserved. Blocks with no blank in them are never
 * touched, which is every block of a properly filled-in template.
 */
export function sanitizeClientTermsHtml(html?: string | null): string {
  if (!html) return '';

  let body: HTMLElement | null = null;
  try {
    body = new DOMParser().parseFromString(html, 'text/html').body;
  } catch {
    // No DOM available: fall back to the token-only strip rather than showing a
    // raw "[__]" — weaker, but never worse than doing nothing.
    return hasBlankPlaceholder(html) ? html.replace(BLANK_PLACEHOLDER_GLOBAL, '') : html;
  }
  if (!body) {
    return hasBlankPlaceholder(html) ? html.replace(BLANK_PLACEHOLDER_GLOBAL, '') : html;
  }

  // Test the TEXT the customer will read, not the markup. The editor can split a
  // blank across inline tags — `[<strong>__</strong>]%` — which no pattern over
  // the raw string matches, and an early return there shipped the placeholder
  // to the customer intact. textContent is what they actually see.
  //
  // Nothing to do in the overwhelmingly common case, and the ORIGINAL string is
  // returned rather than a reserialization, so ordinary terms still reach the
  // customer as exactly the markup that was stored.
  if (!hasBlankPlaceholder(body.textContent ?? '')) return html;

  const clauseBlocks = Array.from(body.querySelectorAll(CLAUSE_BLOCK_SELECTOR)).filter(
    // Innermost only: a <blockquote> wrapping <p>s is handled through the <p>s.
    (el) => el.querySelector(CLAUSE_BLOCK_SELECTOR) === null,
  );

  for (const block of clauseBlocks) {
    const text = block.textContent ?? '';
    if (!hasBlankPlaceholder(text)) continue;

    const kept = sanitizeClientTerms(text);
    if (!kept.trim()) {
      block.remove();
      continue;
    }
    block.textContent = kept;
  }

  pruneEmptyContainers(body);

  // A blank outside any clause block (bare top-level text, a stray inline
  // wrapper) has no clause to drop — remove the token itself where it sits
  // contiguously in the markup.
  if (hasBlankPlaceholder(body.textContent ?? '')) {
    body.innerHTML = body.innerHTML.replace(BLANK_PLACEHOLDER_GLOBAL, '');
  }

  // If that blank was ALSO split across tags, the token strip can't see it
  // either. Last resort: drop the ruined clause and keep the rest as text. The
  // promise — the customer never reads a half-written clause — outranks
  // preserving markup for a fragment that never sat in a block to begin with.
  if (hasBlankPlaceholder(body.textContent ?? '')) {
    body.textContent = sanitizeClientTerms(body.textContent ?? '');
  }

  return body.innerHTML;
}

/**
 * Elements that are content even with no text of their own. A terms block can
 * legitimately be a rule or an image (a scanned clause, a signature block); it
 * cannot legitimately be an empty paragraph.
 */
const CONTENT_BEARING_SELECTOR = 'img, hr, table, iframe, video';

/**
 * Does this markup put anything in front of the customer?
 *
 * `Boolean(html.trim())` — what this used to be — says yes to `<p></p>`, which
 * is exactly what the rich-text editor stores for a terms field someone opened
 * and left alone. That rendered an empty Terms & Conditions card, and on the
 * proposal it also told the customer they were agreeing to terms "below" and
 * offered a jump link to nothing. Emptiness has to be judged after parsing.
 */
export function hasVisibleHtmlContent(html: string): boolean {
  if (!html.trim()) return false;

  let body: HTMLElement | null = null;
  try {
    body = new DOMParser().parseFromString(html, 'text/html').body;
  } catch {
    body = null;
  }
  // No DOM: strip tags and judge what's left, rather than calling it non-empty.
  if (!body) return html.replace(/<[^>]*>/g, '').trim() !== '';

  // `trim()` also drops &nbsp; (U+00A0), which is what the editor leaves behind
  // when a customer-facing block is "cleared" by selecting and deleting.
  if ((body.textContent ?? '').trim() !== '') return true;
  return body.querySelector(CONTENT_BEARING_SELECTOR) !== null;
}

/**
 * Is there anything here worth giving a heading to? Markup-aware, so `<p></p>`
 * and a stray `&nbsp;` read as empty. Use this wherever a section is gated on
 * "does this field have content" — a raw `.trim()` on editor HTML is always
 * wrong, and produces a heading standing over blank space.
 *
 * Prefer {@link resolveCustomerTerms} on customer-facing surfaces: it makes this
 * same judgement AND strips unfilled clauses. This is the bare check, for
 * internal views that must show the stored text verbatim.
 *
 * Narrows: true here means the value is a string with something in it, so the
 * guarded block can hand it straight to a renderer without re-checking. That
 * matters at the call sites this replaced — `value?.trim() &&` narrowed, and a
 * plain boolean helper would have made every one of them widen back to
 * `string | null | undefined`.
 */
export function hasReadableContent(value?: string | null): value is string {
  if (!value) return false;
  return isHtmlContent(value) ? hasVisibleHtmlContent(value) : value.trim() !== '';
}

export interface ResolvedCustomerTerms {
  /** Sanitized body — HTML or plain text depending on `isHtml`. */
  text: string;
  /** True when `text` is markup and must be rendered as such, not escaped. */
  isHtml: boolean;
  /** False when nothing survived sanitizing — the section should not render. */
  hasTerms: boolean;
}

/**
 * The one decision every customer-facing terms surface has to make: is this
 * markup, and what is safe to show? Estimate PDF, invoice PDF, public quote,
 * public invoice and public proposal each had their own copy of this branch,
 * and they drifted — the proposal page never rendered HTML at all, and the two
 * invoice surfaces skipped the blank-placeholder guard entirely.
 */
export function resolveCustomerTerms(raw?: string | null): ResolvedCustomerTerms {
  const source = raw ?? '';
  const isHtml = source !== '' && isHtmlContent(source);
  const text = isHtml ? sanitizeClientTermsHtml(source) : sanitizeClientTerms(source);
  return {
    text,
    isHtml,
    hasTerms: isHtml ? hasVisibleHtmlContent(text) : Boolean(text.trim()),
  };
}
